/**
 * ColourMatch Pro · Business Profile — single tabbed page.
 *
 * Wraps the existing PainterProfile / PainterGallery / PainterAreas /
 * PainterCredentials editors as tabs. Each tab is a separate nested
 * route so URLs are shareable and refreshable.
 *
 * Tab order is founder-locked: Details / Branding / Gallery / Areas /
 * Credentials / Public Profile. See COLOURMATCH_PRO_LOCKED.md.
 *
 * Framing rule: the public listing is presented as an OPTIONAL benefit
 * of membership. This page is not "manage your directory listing" — it
 * is "your business identity inside ColourMatch Pro".
 */
import { NavLink, Outlet, useOutletContext } from "react-router-dom";
import { Building2, Palette, Image as ImageIcon, MapPin, ShieldCheck, ExternalLink } from "lucide-react";

const TABS = [
  { to: "details",     label: "Business Details", icon: Building2,   end: false },
  { to: "branding",    label: "Branding",         icon: Palette,     end: false },
  { to: "gallery",     label: "Gallery",          icon: ImageIcon,   end: false },
  { to: "areas",       label: "Service Areas",    icon: MapPin,      end: false },
  { to: "credentials", label: "Credentials",      icon: ShieldCheck, end: false },
  { to: "public",      label: "Public Profile",   icon: ExternalLink, end: false },
];

export default function BusinessProfile() {
  const ctx = useOutletContext();
  const me = ctx?.me;
  const completeness = me?.profile_completeness ?? null;

  return (
    <div data-testid="pro-business-profile">
      <header className="mb-6">
        <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
        <h1 className="font-serif text-3xl text-brand-ink leading-tight">Business Profile</h1>
        <p className="text-sm text-brand-muted mt-2 max-w-2xl leading-relaxed">
          Your business identity inside ColourMatch Pro. Gallery, service areas and credentials
          are optional — complete them when you&rsquo;re ready.
        </p>
      </header>

      {/* Tab strip */}
      <nav className="mb-6 border-b border-brand-linen overflow-x-auto" data-testid="business-profile-tabs">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`bp-tab-${t.to}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                  isActive
                    ? "border-brand-teal text-brand-teal font-semibold"
                    : "border-transparent text-brand-muted hover:text-brand-ink"
                }`
              }
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Optional subtle completeness prompt — confined to Business Profile per lock. */}
      {completeness != null && completeness < 100 && (
        <div className="mb-6 rounded-xl border border-brand-linen bg-white p-4 flex items-center gap-4" data-testid="bp-completeness">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-brand-muted mb-1.5">Business profile · {completeness}% complete</div>
            <div className="w-full h-1.5 bg-brand-linen rounded-full overflow-hidden">
              <div className="h-full bg-brand-teal transition-all" style={{ width: `${completeness}%` }} />
            </div>
          </div>
          <div className="text-[11px] text-brand-muted italic hidden sm:block">
            Optional — complete when you&rsquo;re ready.
          </div>
        </div>
      )}

      {/* Tab content */}
      <Outlet context={ctx} />
    </div>
  );
}
